// The "Payment details" panel on a subcontractor's page (Session ARZ, Item 3; Director decisions D-SBD-1 to D-SBD-5).
//
// What the office sees: the account name and MASKED numbers (sort code **-**-45, account ****5678), where they came from,
// whether a person has verified them and how, whether they were sent to Xero, and the earlier sets. What it can do:
//   - Verify (office, admin): after phoning the subcontractor on a number that did NOT arrive with these details, record
//     how it was checked. The panel's version stamp goes with it, so a set that arrived during the call is never verified.
//   - Reveal (admin): the full numbers for 60 seconds, audited every time; hidden early if the tab is put away.
//   - Edit (admin): enter details from a verified source (a note says which); they land UNVERIFIED like any other set.
//   - Request payment details (office, admin): a one-time secure link, left as a Gmail DRAFT for the office to send
//     (Session RC2; it was emailed until then). The email never asks for details in a reply.
//   - Clear (admin): the delete. The record of earlier sets is kept (masks only).
// A CHANGE to existing details is shown as a possible fraud until verified (D-SBD-4); the office has also been emailed.
//
// House rules: plain English for somebody with no banking knowledge, no double hyphen, no em dash. Colours are the app
// tokens (amber #b8860b for "attention", the danger token for a change, green for verified).
//
// SESSION AUV (design system Session 8): presentation only. The primary buttons are the shared Button, the loading state
// is the shared Loader and the dates are lib/format.ts (London time, "5 Oct 2026" and "5 Oct 2026, 14:05"). What the panel
// shows, hides, masks and refuses is exactly as before; its tests are unchanged.
import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { Button } from "../../components/Button";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Loader } from "../../components/Loader";
import { formatDate, formatDateTime } from "../../lib/format";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { XeroContactButton } from "./SubcontractorXeroContact";
import {
  EMPTY_PAYMENT_DETAILS,
  paymentDetailsErrors,
  type PaymentDetailsField,
  type PaymentDetailsForm,
  type PaymentDetailsView,
  type RevealedPaymentDetails,
} from "../../lib/subcontractorPayments";

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const btnCls = "rounded-ele border border-ele-purple-accent px-3 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong disabled:opacity-50";
const REVEAL_SECONDS = 60;

export interface PaymentDetailsSubject {
  id: string;
  company: string;
  email: string;
  archivedAt: string | null;
}

export function SubcontractorPaymentDetailsSection({
  sub,
  onChanged,
  reloadKey = 0,
}: {
  sub: PaymentDetailsSubject;
  onChanged?: () => void;
  // Bumped by the page when something outside this panel changed what it shows (a push from the Accounting card).
  reloadKey?: number;
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [view, setView] = useState<PaymentDetailsView | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [copyLink, setCopyLink] = useState<string | null>(null);
  const [dialog, setDialog] = useState<null | "verify" | "edit" | "clear">(null);
  const [busy, setBusy] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoadError(false);
    api.subcontractors
      .paymentDetails(sub.id)
      .then(setView)
      .catch(() => setLoadError(true));
  }, [sub.id]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const adopt = (next: PaymentDetailsView) => {
    setView(next);
    onChanged?.();
  };

  async function requestLink() {
    setBusy(true);
    setError(null);
    setNotice(null);
    setCopyLink(null);
    try {
      const res = await api.subcontractors.requestPaymentDetails(sub.id);
      adopt(res.view);
      if (res.drafted) {
        setNotice(
          `A request to ${sub.email} is waiting in Gmail Drafts for you to send. Its secure link works once and expires on ${formatDate(res.expiresAt)}. ${res.earlierDraftNote}.`,
        );
      } else {
        setCopyLink(res.url);
        setNotice(
          `${res.draftError ?? "No draft was made"}. Copy the secure link below and send it to them another way (never ask for bank details by email). ${res.earlierDraftNote}.`,
        );
      }
    } catch (e) {
      setError(friendlyApiError(e, "Could not make the request. Please try again in a moment."));
    } finally {
      setBusy(false);
    }
  }

  async function clearDetails() {
    if (!view?.current) return;
    setBusy(true);
    setClearError(null);
    try {
      adopt(await api.subcontractors.clearPaymentDetails(sub.id, view.current.version));
      setDialog(null);
      setNotice("The payment details were cleared. The record of them is kept below. Xero was not changed.");
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // The set changed (or went) while the dialog was open. Close it rather than leave it armed over a set nobody has
        // looked at, as Verify does (the Item 3 review).
        setDialog(null);
        setError("The payment details have changed since you opened this page. Look at the new details below before doing anything.");
        load();
      } else {
        setClearError(friendlyApiError(e, "Could not clear the payment details."));
      }
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <Panel>
        <p role="alert" className="text-sm text-ele-strong">
          Could not load the payment details.{" "}
          <button type="button" onClick={load} className="text-ele-blue underline">
            Try again
          </button>
        </p>
      </Panel>
    );
  }
  if (!view) {
    return (
      <Panel>
        <Loader label="Loading the payment details..." />
      </Panel>
    );
  }

  const cur = view.current;
  // Asking for NEW details when verified ones are on file is an admin's decision (decision D110); the server refuses it too.
  const canRequest = !sub.archivedAt && view.storageAvailable && (isAdmin || !cur?.verifiedAt);

  return (
    <Panel>
      {!view.storageAvailable && (
        <Banner tone="amber">
          Payment details cannot be stored on this server yet, because the encryption key is not set. Nothing can be
          requested or entered until it is.
        </Banner>
      )}

      <StateBanner view={view} />

      {cur ? (
        <dl className="mb-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          <Row label="Name on the account" value={cur.accountName} />
          <Row label="Sort code" value={cur.sortCodeMasked} mono />
          <Row label="Account number" value={cur.accountNumberMasked} mono />
          <Row label="Received" value={`${formatDateTime(cur.submittedAt)}${cur.submittedByName ? `, by ${cur.submittedByName}` : ""}`} />
          <Row label="Where from" value={cur.sourceNote ? `${cur.sourceLabel}: ${cur.sourceNote}` : cur.sourceLabel} wide />
          {cur.verifiedAt && (
            <Row label="Verified" value={`${formatDateTime(cur.verifiedAt)}${cur.verifiedByName ? ` by ${cur.verifiedByName}` : ""}: ${cur.verificationNote ?? ""}`} wide />
          )}
        </dl>
      ) : (
        <p className="mb-3 text-sm text-ele-body">
          No payment details on file. Ask the subcontractor to add them on a secure link: the platform never takes bank
          details by email.
        </p>
      )}

      {cur && isAdmin && <RevealBox key={`${sub.id}:${cur.version}`} subId={sub.id} />}

      <XeroLine view={view} />
      {view.xero.linked && cur?.verifiedAt && !cur.pushedToXeroAt && (
        <div className="mb-3">
          <XeroContactButton
            subId={sub.id}
            label="Update Xero contact"
            onDone={() => {
              load();
              onChanged?.();
            }}
          />
        </div>
      )}

      {view.request.active && (
        <p className="mb-3 text-sm text-ele-body">
          A secure link was issued on {formatDate(view.request.requestedAt)} and works until {formatDate(view.request.expiresAt)}.
        </p>
      )}

      {notice && <p className="mb-3 rounded-ele border border-ele-green bg-ele-surface px-3 py-2 text-sm text-ele-strong">{notice}</p>}
      {copyLink && <CopyLinkRow url={copyLink} />}
      {error && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {cur && !cur.verifiedAt && (
          <Button onClick={() => setDialog("verify")} disabled={busy}>
            Verify
          </Button>
        )}
        {canRequest && (
          <ConfirmButton
            label={cur ? "Request new payment details" : "Request payment details"}
            confirmLabel={`Draft a secure link to ${sub.email} in Gmail?`}
            busy={busy}
            busyLabel="Drafting..."
            onConfirm={requestLink}
            className={btnCls}
            armedClassName="!text-ele-strong font-semibold"
          />
        )}
        {isAdmin && view.storageAvailable && (
          <button type="button" onClick={() => setDialog("edit")} disabled={busy} className={btnCls}>
            {cur ? "Replace details" : "Enter details"}
          </button>
        )}
        {isAdmin && cur && (
          <button type="button" onClick={() => setDialog("clear")} disabled={busy} className={`${btnCls} hover:!text-ele-danger`}>
            Clear
          </button>
        )}
      </div>

      <HistoryList view={view} />

      {dialog === "verify" && cur && (
        <VerifyModal
          subId={sub.id}
          userId={user?.id ?? null}
          view={view}
          onClose={() => setDialog(null)}
          onDone={(next, message) => {
            adopt(next);
            setDialog(null);
            setNotice(message);
          }}
          onStale={() => {
            setDialog(null);
            setError("The payment details have changed since you opened this page. Look at the new details below before doing anything.");
            load();
          }}
        />
      )}
      {dialog === "edit" && (
        <EditModal
          subId={sub.id}
          replacing={!!cur}
          onClose={() => setDialog(null)}
          onDone={(next, message) => {
            adopt(next);
            setDialog(null);
            setNotice(message);
          }}
        />
      )}
      <ConfirmDeleteModal
        open={dialog === "clear"}
        title="Clear payment details"
        message={`Remove ${sub.company}'s payment details from the platform? A record of them (the last digits only) is kept. This does not change Xero: if they were sent to Xero, remove them there too.`}
        confirmLabel="Clear"
        busy={busy}
        error={clearError}
        onConfirm={clearDetails}
        onCancel={() => {
          setClearError(null);
          setDialog(null);
        }}
      />
    </Panel>
  );
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <section className="mb-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-4" aria-label="Payment details">
      <h2 className="mb-3 font-heading text-lg uppercase tracking-wide text-ele-blue">Payment details</h2>
      {children}
    </section>
  );
}

function Banner({ tone, children }: { tone: "amber" | "red" | "green"; children: React.ReactNode }) {
  const border = tone === "red" ? "border-ele-danger" : tone === "green" ? "border-ele-green" : "border-ele-amber-line";
  return <div role={tone === "red" ? "alert" : undefined} className={`mb-3 rounded-ele border ${border} bg-ele-surface px-3 py-2 text-sm text-ele-strong`}>{children}</div>;
}

function StateBanner({ view }: { view: PaymentDetailsView }) {
  const cur = view.current;
  if (!cur) return null;
  if (!cur.verifiedAt && cur.changedFromPrevious) {
    return (
      <Banner tone="red">
        <strong>These bank details CHANGED on {formatDate(cur.submittedAt)} and are not verified.</strong> Treat this as a
        possible fraud until you have checked it: phone the subcontractor on a number you already had before this change
        and ask them to confirm it. Do not pay to the new account until then. Xero keeps the previous details.
      </Banner>
    );
  }
  if (!cur.verifiedAt) {
    return (
      <Banner tone="amber">
        <strong>Unverified.</strong> Phone the subcontractor to confirm these details before the first payment, then
        press Verify. They are not sent to Xero until they are verified.
      </Banner>
    );
  }
  return <Banner tone="green">Verified{cur.verifiedByName ? ` by ${cur.verifiedByName}` : ""} on {formatDate(cur.verifiedAt)}.</Banner>;
}

function Row({ label, value, mono, wide }: { label: string; value: string; mono?: boolean; wide?: boolean }) {
  return (
    <div className={`flex justify-between gap-3 ${wide ? "sm:col-span-2" : ""}`}>
      <dt className="text-ele-body">{label}</dt>
      <dd className={`text-right text-ele-strong ${mono ? "font-mono" : ""}`}>{value}</dd>
    </div>
  );
}

function XeroLine({ view }: { view: PaymentDetailsView }) {
  const x = view.xero;
  const text =
    x.state === "no_contact"
      ? "Not yet a contact in Xero."
      : x.state === "sent"
        ? `Sent to Xero on ${formatDate(x.lastSentAt)}.`
        : x.state === "old_details_in_xero"
          ? `Xero has the old details (sent on ${formatDate(x.lastSentAt)}). Update the Xero contact once the new details are verified.`
          : x.state === "not_sent"
            ? "Not sent to Xero yet. Xero may have no bank details for them, or ones typed in by hand."
            : "Nothing verified to send to Xero yet.";
  return <p className="mb-3 text-sm text-ele-body">Xero: {text}</p>;
}

function HistoryList({ view }: { view: PaymentDetailsView }) {
  if (view.history.length === 0) return null;
  return (
    <details className="mt-4">
      <summary className="cursor-pointer font-menu text-xs uppercase tracking-wide text-ele-body">Earlier payment details ({view.history.length})</summary>
      <ul className="mt-2 space-y-1 text-sm text-ele-body">
        {view.history.map((h, i) => (
          <li key={i}>
            {h.accountName}, {h.sortCodeMasked}, {h.accountNumberMasked}: received {formatDate(h.submittedAt)}
            {h.verifiedAt ? `, verified ${formatDate(h.verifiedAt)}` : ", never verified"}
            {h.pushedToXeroAt ? `, sent to Xero ${formatDate(h.pushedToXeroAt)}` : ""}; {h.endedReason === "cleared" ? "cleared" : "replaced"} {formatDate(h.endedAt)}.
          </li>
        ))}
      </ul>
    </details>
  );
}

function CopyLinkRow({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <input readOnly value={url} aria-label="Secure payment details link" className={`${inputCls} max-w-md font-mono text-xs`} />
      <button
        type="button"
        className={btnCls}
        onClick={() => {
          void navigator.clipboard?.writeText(url).then(() => setCopied(true));
        }}
      >
        {copied ? "Copied" : "Copy link"}
      </button>
    </div>
  );
}

// ---- Reveal (admin): the full numbers for 60 seconds, audited server-side on every press ----
// The countdown re-reads the clock (it is not a tally of ticks), and the numbers are hidden the moment the tab is put away,
// so a revealed set never sits on an unattended screen.
function RevealBox({ subId }: { subId: string }) {
  const [shown, setShown] = useState<(RevealedPaymentDetails & { until: number }) | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const hide = useCallback(() => {
    setShown(null);
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") hide();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (timer.current) clearInterval(timer.current);
    };
  }, [hide]);

  useEffect(() => {
    if (shown && now >= shown.until) hide();
  }, [now, shown, hide]);

  async function reveal() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.subcontractors.revealPaymentDetails(subId);
      const until = Date.now() + (res.visibleForSeconds || REVEAL_SECONDS) * 1000;
      setShown({ ...res, until });
      setNow(Date.now());
      if (timer.current) clearInterval(timer.current);
      timer.current = setInterval(() => setNow(Date.now()), 1000);
    } catch (e) {
      setError(friendlyApiError(e, "Could not show the payment details."));
    } finally {
      setBusy(false);
    }
  }

  if (shown) {
    const seconds = Math.max(0, Math.ceil((shown.until - now) / 1000));
    return (
      <div className="mb-3 rounded-ele border border-ele-amber-line bg-ele-surface px-3 py-2 text-sm text-ele-strong">
        <p className="mb-1">
          Full details (hidden in {seconds} {seconds === 1 ? "second" : "seconds"}; this view has been recorded):
        </p>
        <p className="sr-only" role="status">
          Full payment details shown for 60 seconds. This view has been recorded.
        </p>
        <p className="font-mono">
          {shown.accountName} | Sort code {shown.sortCode} | Account {shown.accountNumber}
        </p>
        <button type="button" onClick={hide} className={`${btnCls} mt-2`}>
          Hide now
        </button>
      </div>
    );
  }
  return (
    <div className="mb-3">
      <button type="button" onClick={reveal} disabled={busy} className={btnCls}>
        {busy ? "Opening..." : "Show full details (recorded)"}
      </button>
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </div>
  );
}

// ---- Verify (office, admin): the call-back, recorded ----
function VerifyModal({
  subId,
  userId,
  view,
  onClose,
  onDone,
  onStale,
}: {
  subId: string;
  userId: string | null;
  view: PaymentDetailsView;
  onClose: () => void;
  onDone: (next: PaymentDetailsView, message: string) => void;
  onStale: () => void;
}) {
  const cur = view.current!;
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (note.trim().length < 5) {
      setError("Say how you checked them, for example: phoned 07xxx (the number we already had), the account holder confirmed.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const next = await api.subcontractors.verifyPaymentDetails(subId, { note: note.trim(), version: cur.version });
      onDone(next, "Payment details verified.");
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.body?.error === "changed") {
        onStale();
        return;
      }
      setError(friendlyApiError(e, "Could not record the verification."));
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Verify payment details" onClose={() => !busy && onClose()} width="max-w-lg" margin="my-8" panelClassName="p-6">
      <h2 className="mb-2 font-heading text-2xl tracking-wide text-ele-strong">Verify payment details</h2>
      <p className="mb-2 text-sm text-ele-body">
        Checking: <span className="font-mono text-ele-strong">{cur.accountName}, {cur.sortCodeMasked}, {cur.accountNumberMasked}</span>
      </p>
      <ol className="mb-3 list-decimal space-y-1 pl-5 text-sm text-ele-body">
        <li>Phone the subcontractor on a number you already had before these details arrived. Never use a number given with the new details, or in an email about them.</li>
        <li>Ask them to read out their sort code and account number, and check they end in the digits shown above. Do not read the numbers out to them.</li>
        <li>Write below who you spoke to, the number you called and where you got it.</li>
      </ol>
      {view.contactPhone && (
        <p className="mb-2 text-sm text-ele-body">
          Phone on file: <span className="text-ele-strong">{view.contactPhone}</span>
          {view.contactPhoneFromRegistrationAt ? ` (typed by the subcontractor on their registration form, ${formatDate(view.contactPhoneFromRegistrationAt)})` : ""}
        </p>
      )}
      {userId && (cur.submittedByUserId === userId || cur.linkRequestedByUserId === userId) && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-amber-line bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {cur.submittedByUserId === userId ? "You entered these details yourself." : "You sent the link these details came back on."} If
          you can, ask a colleague to make the call-back and verify them instead. If you verify them yourself, the record
          will say so.
        </p>
      )}
      {cur.phoneArrivedWithDetails && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          The phone number on file arrived on the same form as these bank details, so it does not prove anything on its
          own. Use a number from somewhere else: an earlier email signature or invoice, their website, or Companies House.
        </p>
      )}
      <label className="block">
        <span className={labelCls}>How you verified them</span>
        <textarea
          aria-label="How you verified them"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          className={inputCls}
          placeholder="For example: phoned 07xxx xxxxxx from their 2025 invoice, spoke to the account holder, confirmed"
        />
      </label>
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={onClose} disabled={busy} className={`${btnCls} ${MODAL_ACTION_BTN}`}>
          Cancel
        </button>
        <Button className={MODAL_ACTION_BTN} onClick={submit} disabled={busy}>
          {busy ? "Saving..." : "Record as verified"}
        </Button>
      </div>
    </ModalShell>
  );
}

// ---- Enter or replace (admin): from a verified source only ----
function EditModal({
  subId,
  replacing,
  onClose,
  onDone,
}: {
  subId: string;
  replacing: boolean;
  onClose: () => void;
  onDone: (next: PaymentDetailsView, message: string) => void;
}) {
  const [form, setForm] = useState<PaymentDetailsForm>(EMPTY_PAYMENT_DETAILS);
  const [sourceNote, setSourceNote] = useState("");
  const [errors, setErrors] = useState<Partial<Record<PaymentDetailsField | "sourceNote", string>>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: PaymentDetailsField) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function submit() {
    const e: Partial<Record<PaymentDetailsField | "sourceNote", string>> = { ...paymentDetailsErrors(form) };
    if (sourceNote.trim().length < 5) e.sourceNote = "Say where these details came from, for example: signed letter on the company's headed paper.";
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.subcontractors.enterPaymentDetails(subId, { ...form, sourceNote: sourceNote.trim() });
      onDone(
        res.view,
        res.unchanged
          ? "These are the details already on file, so nothing was changed."
          : res.changed
            ? "Saved as UNVERIFIED. Because they replace earlier details, the office has been alerted. Verify them by phone before any payment."
            : "Saved as UNVERIFIED. Verify them by phone before the first payment.",
      );
    } catch (err) {
      const field = err instanceof ApiError ? err.body?.field : undefined;
      if (typeof field === "string" && field in EMPTY_PAYMENT_DETAILS && err instanceof ApiError) setErrors({ [field]: err.code });
      else setError(friendlyApiError(err, "Could not save the payment details."));
      setBusy(false);
    }
  }

  const field = (label: string, k: PaymentDetailsField, placeholder?: string) => (
    <label className="block">
      <span className={labelCls}>{label}</span>
      <input aria-label={label} value={form[k]} onChange={(e) => set(k)(e.target.value)} autoComplete="off" spellCheck={false} placeholder={placeholder} className={inputCls} />
      {errors[k] && <span role="alert" className="mt-1 block text-sm text-ele-danger">{errors[k]}</span>}
    </label>
  );

  return (
    <ModalShell ariaLabel={replacing ? "Replace payment details" : "Enter payment details"} onClose={() => !busy && onClose()} width="max-w-lg" margin="my-8" panelClassName="p-6">
      <h2 className="mb-2 font-heading text-2xl tracking-wide text-ele-strong">{replacing ? "Replace payment details" : "Enter payment details"}</h2>
      <p className="mb-3 text-sm text-ele-body">
        Only enter details you have from a verified source, such as a signed letter on their headed paper. Never from an
        email: that is how mandate fraud works. The details are saved as unverified and must be confirmed by phone before
        any payment.
        {replacing ? " Because they replace the details on file, the office will be alerted and Xero keeps the old details until the new ones are verified." : ""}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">{field("Name on the account", "accountName")}</div>
        {field("Sort code", "sortCode", "12-34-56")}
        <div />
        {field("Account number", "accountNumber", "8 digits")}
        {field("Account number again", "accountNumberConfirm")}
        <label className="block sm:col-span-2">
          <span className={labelCls}>Where these details came from</span>
          <textarea aria-label="Where these details came from" value={sourceNote} onChange={(e) => setSourceNote(e.target.value)} rows={2} className={inputCls} />
          {errors.sourceNote && <span role="alert" className="mt-1 block text-sm text-ele-danger">{errors.sourceNote}</span>}
        </label>
      </div>
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}
      <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={onClose} disabled={busy} className={`${btnCls} ${MODAL_ACTION_BTN}`}>
          Cancel
        </button>
        <Button className={MODAL_ACTION_BTN} onClick={submit} disabled={busy}>
          {busy ? "Saving..." : "Save as unverified"}
        </Button>
      </div>
    </ModalShell>
  );
}
